import React, { ChangeEventHandler, FocusEventHandler, useCallback, useContext, useMemo, useState } from 'react'
import { Controller, UseControllerProps } from 'react-hook-form'
import { IconButton, InputAdornment, InputBaseProps, MenuItem, TextField } from '@mui/material'
import { SelectType } from '../../types';
import { AppContext } from '../../context';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface InputFieldProps {
    label: string;
    controller: UseControllerProps;
    errors: any;
    required?: boolean;
    autoFocus?: boolean;
    disabled?: boolean;
    inputProps?: InputBaseProps['inputProps'],
    fieldEvents?: InputFieldEventsType
    options?: SelectType[],
    type?: string,
    variant?: "outlined" | "standard" | "filled",
    helpText?: React.ReactNode,
    startAdornment?: JSX.Element,
    endAdornment?: JSX.Element,
    maxRows?: number,
    'testid'?: string
}

interface InputFieldEventsType {
    onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
    onChange?: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>
    onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>
    onKeyPress?: React.KeyboardEventHandler<HTMLDivElement>
}

export function InputField({
    label,
    controller,
    errors,
    inputProps = {},
    fieldEvents = {},
    autoFocus = false,
    required = false,
    disabled = false,
    options = [],
    type,
    variant = "outlined",
    helpText,
    startAdornment,
    endAdornment,
    maxRows,
    testid
}: InputFieldProps) {
    const appContext = useContext(AppContext)
    const [showHelp, setShowHelp] = useState(false)
    const errorsByName = useCallback(() => {
        let nameParts = controller.name.split(".")
        let error = { ...errors }
        for (let part of nameParts) {
            if (error) {
                error = error[part]
            }
        }
        return error
    }, [controller.name, errors])
    const helperText = useMemo(() => showHelp ? helpText : '', [showHelp])
    return (
        <Controller
            {...controller}
            render={({ field }) => (
                <TextField
                    id={controller.name}
                    label={label}
                    required={required}
                    fullWidth
                    size="small"
                    variant={variant}
                    autoFocus={autoFocus}
                    disabled={disabled || appContext?.loading}
                    error={errorsByName() !== undefined}
                    helperText={errorsByName()?.message || helperText}
                    {...field}
                    inputProps={{ ...inputProps, "data-testid": testid }}
                    InputProps={helpText ? {
                        endAdornment: <InputAdornment position="end">
                            <IconButton onClick={() => setShowHelp(!showHelp)}>
                                <FontAwesomeIcon icon="question" />
                            </IconButton>
                        </InputAdornment>,
                        startAdornment
                    } : { startAdornment, endAdornment }}
                    select={options.length > 0}
                    type={type}
                    onChange={(e) => {
                        field.onChange(e)
                        if (fieldEvents?.onChange) {
                            fieldEvents.onChange(e)
                        }
                    }}
                    onKeyDown={e => fieldEvents?.onKeyDown ? fieldEvents?.onKeyDown(e) : true}
                    multiline={!!maxRows && maxRows > 0}
                    rows={maxRows || 1}
                    maxRows={maxRows || 1}
                >
                    {options.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                </TextField>
            )}
        />
    )
}
